import React, { useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RiPlaneLine, RiArrowUpLine, RiArrowDownSLine, RiLoader4Line, RiCheckboxCircleLine, RiAddLine } from '@remixicon/react';
import { cn } from '../../../../components/ui/utils';
import AircraftCard from './AircraftCard';
import EditPricingDrawer from './EditPricingDrawer';
import { SearchAircraft } from '../../../search';
import { useAppStore } from '@store/appStore';
import Modal from '@components/ui/Modal';

/**
 * AircraftOptionsSection Component
 * Card-style section displaying available aircraft options with selection functionality
 *
 * @param {Object} props
 * @param {Object} props.selectedAircraft - Currently selected aircraft
 * @param {Function} props.onSelect - Callback when aircraft is selected
 * @param {boolean} props.isComplete - Whether section is complete
 * @param {boolean} props.isExpanded - Whether section is expanded
 * @param {Function} props.onToggle - Callback when section is toggled
 * @param {Function} props.onNext - Callback when Next button is clicked
 * @param {Function} props.onBack - Callback when Back button is clicked
 * @param {boolean} props.isUnlocked - Whether section has been unlocked by clicking Next
 * @param {Array} props.aircraft - Aircraft array (optional, for testing)
 * @param {boolean} props.isLoading - Loading state (optional, for testing)
 */
const AircraftOptionsSection = ({
  formData,
  isComplete = false,
  isExpanded = false,
  onToggle,
  onNext,
  onBack,
  isUnlocked = false,
  onUpdate,
  onBatchUpdate,
  isDataLoading
}) => {
  const [searchParams] = useSearchParams();
  const isQuickQuote = searchParams.has('quickquote');
  const { shortlistAircraft } = useAppStore();
  const [showSearchAircraft, setShowSearchAircraft] = useState(false);
  const [showEditPricing, setShowEditPricing] = useState(false);
  const [selectedAircraftForEdit, setSelectedAircraftForEdit] = useState(null);

  // Drag to scroll refs and state for aircraft slider
  const aircraftSliderRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const handleMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.pageX - aircraftSliderRef.current.offsetLeft;
    scrollLeft.current = aircraftSliderRef.current.scrollLeft;
    aircraftSliderRef.current.style.cursor = 'grabbing';
    aircraftSliderRef.current.style.userSelect = 'none';
  };

  const handleMouseLeave = () => {
    isDragging.current = false;
    if (aircraftSliderRef.current) {
      aircraftSliderRef.current.style.cursor = 'grab';
      aircraftSliderRef.current.style.userSelect = '';
    }
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    if (aircraftSliderRef.current) {
      aircraftSliderRef.current.style.cursor = 'grab';
      aircraftSliderRef.current.style.userSelect = '';
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - aircraftSliderRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5;
    aircraftSliderRef.current.scrollLeft = scrollLeft.current - walk;
  };

  // Handle price change for an aircraft
  const handlePriceChange = (optionId, value, field) => {
    // Update bid option property - onUpdate expects (optionId, field, value)
    if (onUpdate) {
      onUpdate(optionId, field, value);
    }
  };

  // Handle edit pricing button click
  const handleEditPricing = (aircraft) => {
    setSelectedAircraftForEdit(aircraft);
    setShowEditPricing(true);
  };

  // Handle save pricing from drawer
  const handleSavePricing = (optionId, updates) => {
    if (onBatchUpdate) {
      // Use batch update for better performance
      onBatchUpdate(optionId, updates);
    } else if (onUpdate) {
      // Fallback to individual updates
      Object.entries(updates).forEach(([field, value]) => {
        onUpdate(optionId, field, value);
      });
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
      {/* Section Header - Always Visible */}
      <button
        type="button"
        onClick={onToggle}
        disabled={!isUnlocked}
        className={cn(
          'w-full flex items-center justify-between p-6 text-left transition-colors',
          (!isUnlocked) && 'cursor-not-allowed opacity-60'
        )}
      >
        <div className="flex items-center gap-3 flex-1">
          <RiPlaneLine className="w-5 h-5 text-gray-700 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900">Aircraft Options and Pricing</h3>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isComplete && (
            <div className="flex items-center justify-center w-6 h-6 rounded-full">
              <RiCheckboxCircleLine className="w-6 h-6 text-primary-500" />
            </div>
          )}
        </div>
      </button>

      {/* Section Content - Shown when expanded */}
      {isExpanded && (
        <div className="px-6 pb-6">
          <div data-testid="section-content">
        {/* Loading State */}
        {isDataLoading && (
          <div className="flex flex-col items-center justify-center py-12">
            <RiLoader4Line
              data-testid="loading-spinner"
              className="h-8 w-8 animate-spin text-blue-600 mb-3"
              aria-hidden="true"
            />
            <p className="text-gray-600" aria-live="polite">
              Loading aircraft options...
            </p>
            {/* Loading Skeletons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 w-full">
              {[1, 2].map(i => (
                <div
                  key={i}
                  data-testid="aircraft-skeleton"
                  className="border border-gray-200 rounded-lg p-4 animate-pulse"
                >
                  <div className="h-32 bg-gray-200 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isDataLoading && (!formData.bidOptions || formData.bidOptions.length === 0) && (
          <div className="flex flex-col items-center justify-center py-12">
            <RiPlaneLine
              data-testid="empty-state-icon"
              className="h-12 w-12 text-gray-400 mb-3"
            />
            <p className="text-gray-900 font-medium mb-1">No aircraft available</p>
            <p className="text-gray-600 mb-4">
              No aircraft match your search criteria
            </p>
          </div>
        )}

        {/* Aircraft Slider */}
        {!isDataLoading && formData.bidOptions && formData.bidOptions.length > 0 && (
          <div
            ref={aircraftSliderRef}
            data-testid="aircraft-grid"
            aria-label="Available aircraft options"
            className="flex gap-4 overflow-x-auto pb-2 -mx-6 px-6 cursor-grab [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
          >
            {formData.bidOptions
              .map(aircraftItem => (
                <div key={aircraftItem.bid_option_id} className="flex-shrink-0 w-80">
                  <AircraftCard
                    aircraft={aircraftItem}
                    passengerCount={formData.flights?.flights?.[0]?.passengerCount || 1}
                    flightType={formData.flights?.type || 'one-way'}
                    flightsLength={formData.flights?.flights?.length || 1}
                    onPriceChange={handlePriceChange}
                    onEditPricing={handleEditPricing}
                  />
                </div>
              ))}
          </div>
        )}

        {isQuickQuote && (
          <button
            type="button"
            onClick={() => setShowSearchAircraft(true)}
            className={cn(
              'w-full mt-4 px-4 py-3 rounded-full text-sm font-medium transition-all border border-primary-700',             'bg-white rounded-full text-primary-700 text-md',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
              'flex items-center justify-center gap-2'
            )}
          >
            <RiAddLine className="w-4 h-4" />
            Add Aircraft
          </button>
        )}

        </div>

          {/* Navigation Buttons */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onBack}
              className={cn(
                'flex-1 px-4 py-3 rounded-full text-sm font-medium transition-all',
                'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                'flex items-center justify-center gap-2',
              )}
            >
              <RiArrowUpLine className="w-4 h-4" />
              Back
            </button>
            <button
              type="button"
              onClick={onNext}
              className={cn(
                'flex-1 px-4 py-3 rounded-full text-sm font-medium transition-all',
                'focus:outline-none focus:ring-0 focus:ring-blue-0 focus:ring-offset-0',
                'flex items-center justify-center gap-2',
                (!formData?.bidOptions || !formData.bidOptions?.length > 0)
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-accent text-primary-500 hover:bg-accent',
              )}
              disabled={!formData?.bidOptions || !formData.bidOptions?.length > 0}
            >
              Next
              <RiArrowDownSLine className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Search Aircraft Modal */}
      <Modal
        isOpen={showSearchAircraft}
        onClose={() => setShowSearchAircraft(false)}
        size="md"
        title="Search Aircraft"
        headerClassName="!border-0"
        closeOnOutsideClick={false}
      >
        <SearchAircraft showHeader={false} onClose={() => setShowSearchAircraft(false)} addtails="add" />
      </Modal>

      {/* Edit Pricing Drawer */}
      <EditPricingDrawer
        isOpen={showEditPricing}
        onClose={() => setShowEditPricing(false)}
        aircraft={selectedAircraftForEdit}
        passengerCount={formData.flights?.flights?.[0]?.passengerCount || 6}
        flightType={formData.flights?.type || 'one-way'}
        flightsLength={formData.flights?.flights?.length || 1}
        onSave={handleSavePricing}
      />
    </div>
  );
};

export default AircraftOptionsSection;
